import { forwardRef, memo, useContext, useEffect, useMemo, useState } from 'react';
import isNil from 'lodash/isNil';
// Skillmore UI Components
import { paramsDiffer } from '@empathco/ui-components/src/helpers/pagination';
import CardsGrid from '@empathco/ui-components/src/components/CardsGrid';
// local imports
import { SkillLevel, SKILL_LEVEL_MIN } from '../models/skill';
import { GlobalContext } from '../context/global';
import { EmployeesWithSkillParams, SupervisorContext } from '../context/supervisor';
import EmployeeCard, { EmployeeCardItem, EmployeeCardProps } from '../v3/EmployeeCard';
import EmployeeTeamCard from '../v3/EmployeeTeamCard';
import PaginationControls from '../v3/PaginationControls';

type EmployeesWithSkillListProps = {
  readonly settingsId?: string;
  readonly skillId: string;
  readonly asTeams: boolean;
  readonly managerId?: string | null;
  readonly level?: SkillLevel | null;
  readonly stateId?: number | null;
  readonly countryId?: number | null;
}

const EmployeesWithSkillList = forwardRef<HTMLDivElement, EmployeesWithSkillListProps>(({
  settingsId,
  skillId,
  asTeams,
  managerId,
  level = SKILL_LEVEL_MIN,
  stateId,
  countryId
}, ref) => {
  const { paths: { supvEmplPath } } = useContext(GlobalContext);
  const {
    mgrEmployeesWithSkill: { data: employees, count, pending: employeesPending, failed: employeesFailed, params },
    requireEmployeesWithSkill,
    mgrTeamsWithSkill: { data: teams, pending: teamsPending, failed: teamsFailed },
    requireTeamsWithSkill
  } = useContext(SupervisorContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>();

  useEffect(() => {
    if (asTeams) {
      requireTeamsWithSkill?.({ skill_id: skillId, manager_id: managerId });
    } else if (requireEmployeesWithSkill && !isNil(pageSize)) {
      const newParams: EmployeesWithSkillParams = {
        skill_id: skillId,
        level,
        state_id: stateId,
        country_id: stateId ? undefined : countryId,
        limit: pageSize
      };
      let curPage = currentPage;
      if (paramsDiffer(params, newParams)) {
        curPage = 1;
        setCurrentPage(1);
      }
      requireEmployeesWithSkill({
        ...newParams,
        offset: pageSize * (curPage - 1)
       });
     }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // only filter values are monitored here (i.e. ignoring `params` changes)
    asTeams, skillId, managerId, level, stateId, countryId, currentPage, pageSize,
    requireTeamsWithSkill, requireEmployeesWithSkill
  ]);

  const pending = asTeams ? teamsPending && !teams : employeesPending && !employees;
  const failed = asTeams ? teamsFailed : employeesFailed;

  const values = useMemo(() => asTeams ? undefined : { level, count }, [asTeams, count, level]);

  const componentProps: Partial<EmployeeCardProps> = useMemo(() => ({
    withSkills: true,
    route: supvEmplPath
  }), [supvEmplPath]);

  return asTeams ? (
    <CardsGrid
        ref={ref}
        items={teams}
        title="skill.teams_list_title"
        values={values}
        variant="shady"
        pending={pending}
        failed={failed}
        withoutTopPadding
        notFoundMessage="employees.no_teams"
        component={EmployeeTeamCard}
    />
  ) : (
    <CardsGrid
        ref={ref}
        items={employees as (EmployeeCardItem[] | null | undefined)}
        title="skill.employees_list_title"
        notFoundMessage="employees.no_direct_reports"
        values={values}
        variant="shady"
        pending={pending}
        failed={failed}
        withoutTopPadding
        component={EmployeeCard}
        ComponentProps={componentProps}
        pagination={(
          <PaginationControls
              settingsId={settingsId}
              loaded={Boolean(employees)}
              pending={pending}
              loading={pending}
              total={count}
              currentPage={currentPage}
              onPageSelected={setCurrentPage}
              onPageSize={setPageSize}
              disabled={pending || failed}
          />
        )}
    />
  );
});

EmployeesWithSkillList.displayName = 'EmployeesWithSkillList';

export default memo(EmployeesWithSkillList);
